import { useCallback, useEffect, useRef, useState } from 'react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import type { CampaignEditorSaveTask } from '@/pages/CampanhasPage/editor/campaignEditorSaveTask'
import {
  apiRouletteError,
  loadApiRoulette,
  rotateApiRouletteKey,
  saveApiRoulette,
} from './api'
import type { ApiRouletteConfig } from './types'
import { ApiRouletteWheel } from './ApiRouletteWheel'
import { ApiRouletteScheduleEditor } from './ApiRouletteScheduleEditor'
import { ApiRouletteLogs } from './ApiRouletteLogs'
import { ApiRouletteSectionHeading } from './ApiRouletteSectionHeading'
import { ApiRouletteSliceTable } from './ApiRouletteSliceTable'
import styles from './ApiRoulette.module.css'
import '@/components/LandingLuckyWheel/LandingLuckyWheel.css'
import './wheel.css'

export function ApiRoulettePanel({
  campaignId,
  onSaveStateChange,
  savingCampaign = false,
}: {
  campaignId: number
  onSaveStateChange?: (task: CampaignEditorSaveTask | null) => void
  savingCampaign?: boolean
}) {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [config, setConfig] = useState<ApiRouletteConfig | null>(null)
  const [snapshot, setSnapshot] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [key, setKey] = useState<string | null>(null)
  const [keyBusy, setKeyBusy] = useState(false)
  const [confirmRotation, setConfirmRotation] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState('')
  const [retry, setRetry] = useState(0)
  const saveLock = useRef(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    setKey(null)
    void loadApiRoulette(campaignId)
      .then((data) => {
        if (alive) {
          setConfig(data)
          setSnapshot(JSON.stringify(data))
        }
      })
      .catch((err: unknown) => {
        if (alive) setError(apiRouletteError(err))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [campaignId, retry])

  const hasChanges = config !== null && JSON.stringify(config) !== snapshot
  const save = useCallback(async () => {
    if (!config || saveLock.current) return false
    if (!hasChanges) return true
    saveLock.current = true
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await runWithStepUp(() => saveApiRoulette(campaignId, config))
      setConfig(result)
      setSnapshot(JSON.stringify(result))
      setSuccess('Configurações e horários da Roleta API salvos.')
      return true
    } catch (err) {
      setError(apiRouletteError(err))
      return false
    } finally {
      saveLock.current = false
      setSaving(false)
    }
  }, [campaignId, config, hasChanges, runWithStepUp])

  useEffect(() => {
    onSaveStateChange?.({ hasChanges, saving, save })
  }, [hasChanges, saving, save, onSaveStateChange])
  useEffect(() => () => onSaveStateChange?.(null), [onSaveStateChange])

  if (loading) return <p role="status">Carregando Roleta API…</p>
  if (!config)
    return (
      <div role="alert">
        <p>{error ?? 'Não foi possível carregar a Roleta API.'}</p>
        <button type="button" onClick={() => setRetry(retry + 1)}>
          Tentar novamente
        </button>
      </div>
    )
  const disabled = saving || savingCampaign
  const change = (patch: Partial<ApiRouletteConfig>) => {
    setConfig({ ...config, ...patch })
    setSuccess(null)
  }
  const setCount = (count: number) => {
    if (!Number.isInteger(count) || count < 2 || count > 16) return
    change({
      slices: Array.from(
        { length: count },
        (_, index) =>
          config.slices[index] ?? {
            id: crypto.randomUUID(),
            label: `Fatia ${index + 1}`,
            description: '',
            kind: 'lose' as const,
            color: index % 2 ? '#f2edff' : '#5e17eb',
            text_color: index % 2 ? '#311260' : '#ffffff',
          }
      ),
    })
  }
  const rotate = async () => {
    if (keyBusy || !config.version || (config.key_configured && !confirmRotation)) return
    setKeyBusy(true)
    setError(null)
    try {
      const response = await runWithStepUp(() => rotateApiRouletteKey(campaignId))
      setKey(response.key)
      setConfirmRotation(false)
      setConfig((current) => (current ? { ...current, key_configured: true } : current))
      setSnapshot((current) =>
        JSON.stringify({ ...JSON.parse(current), key_configured: true })
      )
    } catch (err) {
      setError(apiRouletteError(err))
    } finally {
      setKeyBusy(false)
    }
  }
  return (
    <div className={styles.root}>
      <div className={styles.intro}>
        <div className={styles.introHeading}>
          <div>
            <span className={styles.eyebrow}>EXPERIÊNCIA NO SEU SITE</span>
            <h2>Roleta API</h2>
          </div>
          <span
            className={
              hasChanges
                ? styles.statusPending
                : config.enabled
                  ? styles.statusActive
                  : styles.statusInactive
            }
          >
            {hasChanges
              ? 'Alterações não salvas'
              : config.enabled
                ? 'Roleta ativa'
                : 'Roleta desativada'}
          </span>
        </div>
        <p>
          Monte a roleta, escolha quando cada prêmio será liberado e conecte a experiência
          ao seu site.
        </p>
        <ol className={styles.overviewSteps} aria-label="Etapas da Roleta API">
          <li>
            <span>01</span> Aparência e fatias
          </li>
          <li>
            <span>02</span> Horários vencedores
          </li>
          <li>
            <span>03</span> Integração
          </li>
          <li>
            <span>04</span> Participações
          </li>
        </ol>
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className={styles.success} role="status">
          {success}
        </p>
      )}
      <section className={styles.section}>
        <ApiRouletteSectionHeading
          step="01"
          title="Aparência e fatias"
          actions={
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={config.enabled}
                disabled={disabled}
                onChange={(e) => change({ enabled: e.target.checked })}
              />
              Ativar Roleta API
            </label>
          }
        >
          Defina o visual e o conteúdo da roleta. Os horários da próxima etapa determinam
          quem ganha.
        </ApiRouletteSectionHeading>
        <fieldset
          disabled={disabled}
          className={`${styles.fields} ${styles.identityFields}`}
        >
          <legend className={styles.srOnly}>Título e quantidade de fatias</legend>
          <label>
            Título da roleta
            <input
              value={config.appearance.title}
              maxLength={120}
              onChange={(e) =>
                change({ appearance: { ...config.appearance, title: e.target.value } })
              }
            />
          </label>
          <label className={styles.sliceCount}>
            Quantidade de fatias
            <select
              value={config.slices.length}
              onChange={(event) => setCount(Number(event.target.value))}
            >
              {Array.from({ length: 15 }, (_, index) => index + 2).map((count) => (
                <option key={count} value={count}>
                  {count} fatias
                </option>
              ))}
            </select>
          </label>
        </fieldset>
        <div className={styles.designGrid}>
          <fieldset disabled={disabled} className={styles.fields}>
            <legend className={styles.srOnly}>Cores da roleta</legend>
            <div className={styles.grid}>
              {(
                [
                  ['primary_color', 'Cor da roleta'],
                  ['button_color', 'Cor do botão'],
                  ['button_text_color', 'Texto do botão'],
                ] as const
              ).map(([field, label]) => (
                <label key={field}>
                  {label}
                  <span className={styles.colorControl}>
                    <input
                      type="color"
                      value={config.appearance[field]}
                      onChange={(e) =>
                        change({
                          appearance: { ...config.appearance, [field]: e.target.value },
                        })
                      }
                    />
                    <code aria-hidden="true">
                      {config.appearance[field].toUpperCase()}
                    </code>
                  </span>
                </label>
              ))}
            </div>
            <p className={styles.helperNote}>
              Veja as alterações na prévia ao lado. A quantidade de fatias não representa
              a probabilidade de ganhar.
            </p>
          </fieldset>
          <div className={styles.preview}>
            <span className={styles.previewLabel}>Prévia da roleta</span>
            <ApiRouletteWheel
              configuration={config}
              preview
              key={JSON.stringify(config.appearance) + JSON.stringify(config.slices)}
            />
          </div>
        </div>
        <ApiRouletteSliceTable
          slices={config.slices}
          disabled={disabled}
          onChange={(slices) => change({ slices })}
        />
      </section>
      <ApiRouletteScheduleEditor
        slots={config.slots}
        slices={config.slices}
        timezone={config.timezone}
        disabled={disabled}
        onChange={(slots) => change({ slots })}
        onLog={setSelectedSlot}
      />
      <section className={`${styles.section} ${styles.integrationSection}`}>
        <ApiRouletteSectionHeading step="03" title="Integração com o site externo">
          Autorize o endereço do seu site e gere a chave para conectar a roleta.
        </ApiRouletteSectionHeading>
        <label>
          Sites autorizados
          <textarea
            rows={3}
            disabled={disabled}
            placeholder="https://campanha.exemplo.com.br"
            value={config.allowed_origins.join('\n')}
            onChange={(e) => change({ allowed_origins: e.target.value.split('\n') })}
            onBlur={() =>
              change({
                allowed_origins: config.allowed_origins
                  .map((origin) => origin.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
        <p className={styles.helperNote}>
          Uma origem HTTPS por linha, sem caminho nem barra no final. Exemplo:
          https://campanha.exemplo.com.br
        </p>
        <div className={styles.integrationCredentials}>
          <div className={styles.subsectionHeading}>
            <h4>Chave da integração</h4>
            <span className={styles.badge}>
              {config.key_configured ? 'Chave configurada' : 'Chave ainda não gerada'}
            </span>
          </div>
          <p className={styles.helperNote}>
            A chave deve ficar somente no servidor do seu site. Cada participante recebe
            uma autorização de giro válida por cinco minutos.
          </p>
          <p className={styles.helperNote}>
            Salve a configuração antes de gerar a chave. Uma nova chave revoga a anterior
            e todas as autorizações ainda pendentes.
          </p>
          {config.key_configured && (
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={confirmRotation}
                onChange={(e) => setConfirmRotation(e.target.checked)}
              />
              Entendi que substituir a chave interrompe a integração anterior.
            </label>
          )}
          <button
            type="button"
            disabled={
              disabled ||
              hasChanges ||
              keyBusy ||
              !config.version ||
              (config.key_configured && !confirmRotation)
            }
            onClick={() => void rotate()}
          >
            {keyBusy
              ? 'Gerando…'
              : config.key_configured
                ? 'Substituir chave da integração'
                : 'Gerar chave da integração'}
          </button>
          {key && (
            <div className={styles.secret} role="status">
              <strong>Copie agora. A chave não será exibida novamente.</strong>
              <code>{key}</code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(key)
                    .then(() =>
                      setSuccess('Chave copiada. Guarde no servidor da integração.')
                    )
                    .catch(() =>
                      setError(
                        'Não foi possível copiar. Selecione e copie a chave manualmente.'
                      )
                    )
                }}
              >
                Copiar chave
              </button>
              <button type="button" onClick={() => setKey(null)}>
                Ocultar chave
              </button>
            </div>
          )}
        </div>
        <details className={styles.integrationDetails}>
          <summary>Informações para conectar a roleta</summary>
          <p className={styles.helperNote}>
            API: <code>/api/roulette-api/{campaignId}/tickets</code> para autorizar no
            servidor e <code>/spin</code> para girar no navegador. O componente externo
            reutiliza a roda e a animação existentes.
          </p>
          <p className={styles.helperNote}>
            Os prêmios ficam registrados para entrega pela sua integração.
          </p>
        </details>
      </section>
      <div className={styles.saveBar}>
        <span>
          {hasChanges ? 'Há alterações para salvar' : 'Configuração salva'} · revisão{' '}
          {config.version}
        </span>
        <button
          type="button"
          disabled={disabled || !hasChanges}
          onClick={() => void save()}
        >
          {saving ? 'Salvando…' : 'Salvar Roleta API'}
        </button>
      </div>
      {config.version > 0 && (
        <ApiRouletteLogs
          key={`${campaignId}:${selectedSlot}`}
          campaignId={campaignId}
          selectedSlot={selectedSlot}
          onSlotChange={setSelectedSlot}
          revision={config.version}
          timezone={config.timezone}
        />
      )}
    </div>
  )
}
